import {
    Alert,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { round2 } from "../format-utils"
import { FacilityDrillDownRow } from "./facility-drill-down-row"
import { CostFigure } from "./cost-figure"

export const FREQUENCY_LABELS = {
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
    quarterly: "3 Monthly",
    biannual: "6 Monthly",
    yearly: "Yearly+",
}

export const FREQUENCY_ORDER = ["daily", "weekly", "monthly", "quarterly", "biannual", "yearly"]

// Presentational shell shared by the legacy (inline arrays) and database-backed
// (server aggregate) facility drill-downs.
export function FacilityDrillDownContent({
    facility,
    currencySymbol,
    taskCount,
    criticalityBreakdown,
    frequencyBreakdown,
    loading,
    error,
    onClose,
}) {
    return (
        <>
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="lucide:building" sx={{ fontSize: 24 }} />
                    <Typography variant="h6">{facility?.facilityName}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ minWidth: 500 }}>
                <If value={!!error}>
                    <Alert severity="error" data-cy="facility-drill-down-error">
                        {error?.message || "Could not load this facility's detail. Reload the scenario to try again."}
                    </Alert>
                    <Else>
                        <If value={loading}>
                            <Stack alignItems="center" sx={{ py: 6 }}>
                                <CircularProgress size={28} />
                            </Stack>
                            <Else>
                                <Stack spacing={3} sx={{ pt: 1 }}>
                                    <Stack direction="row" spacing={4}>
                                        <Stack spacing={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                                Total Cost
                                            </Typography>
                                            <CostFigure
                                                value={facility?.totalCost}
                                                currencySymbol={currencySymbol}
                                                variant="h5"
                                            />
                                        </Stack>
                                        <Stack spacing={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                                Total Hours
                                            </Typography>
                                            <Typography variant="h5">{round2(facility?.totalHours)}h</Typography>
                                        </Stack>
                                        <Stack spacing={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                                Tasks
                                            </Typography>
                                            <Typography variant="h5" data-cy="facility-drill-down-task-count">
                                                {taskCount}
                                            </Typography>
                                        </Stack>
                                    </Stack>

                                    <If value={criticalityBreakdown.length > 0}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                By Criticality
                                            </Typography>
                                            <Box
                                                sx={{
                                                    border: 1,
                                                    borderColor: "divider",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <Repeat
                                                    list={criticalityBreakdown}
                                                    item={<FacilityDrillDownRow currencySymbol={currencySymbol} />}
                                                />
                                            </Box>
                                        </Box>
                                    </If>

                                    <If value={frequencyBreakdown.length > 0}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                By Visit Frequency
                                            </Typography>
                                            <Box
                                                sx={{
                                                    border: 1,
                                                    borderColor: "divider",
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <Repeat
                                                    list={frequencyBreakdown}
                                                    item={<FacilityDrillDownRow currencySymbol={currencySymbol} />}
                                                />
                                            </Box>
                                        </Box>
                                    </If>
                                </Stack>
                            </Else>
                        </If>
                    </Else>
                </If>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" data-cy="facility-drill-down-close">
                    Close
                </Button>
            </DialogActions>
        </>
    )
}
